import React, { createContext, useCallback, useState } from "react";
import {
  generateConciliationTransactionsMatch,
  pageFinancialTransactionsInstallmetnsForConciliation,
} from "../../services/conciliationTransactionService";
import { transformCamelCaseToSnakeCase } from "../../utils/stringUtils";
import { useSelector } from "react-redux";
import moment from "moment";

const CONCILIATION_CONTEXT_STATE = {
  isModalSelectFinancialInstallmentsVisible: false,
  showModalSelectFinancialInstallments: (conciliationTransaction) => {},
  closeModalSelectFinancialInstallments: () => {},
  selectedConciliationTransaction: null,
  // Table conciliation properties
  selectedRowsKeys: [],
  setSelectedRowKeys: () => {},
  selectedRows: [],
  setSelectedRows: () => {},
  dataList: [],
  isLoadingDataList: false,
  tableProperties: {
    size: 10,
    page: 0,
    tableFilters: {},
    tableSorters: {},
    search: "",
  },
  fetchData: (page = 0, size = 10, sort = {}, search = "") => {},
  handleTableChange: (pagination = null, filters = null, sorter = null) => {},
  // Table select financial installments properties
  dataListFinancialInstallments: [],
  isLoadingDataListFinancialInstallments: false,
  tablePropertiesFinancialInstallments: {
    size: 10,
    page: 0,
    tableFilters: {},
    tableSorters: {},
    search: "",
  },
  fetchDataFinancialInstallments: (
    page = 0,
    size = 10,
    sort = {},
    search = "",
    idsNotIncluded = [],
  ) => {},
  handleTableChangeFinancialInstallments: (
    pagination = null,
    filters = null,
    sorter = null,
  ) => {},
  updateStatementTransactions: (keys, records) => {},
  removeFinancialTransactionInstallment: (
    statementId,
    financialTransactionMainInstallmentId,
  ) => {},
};

export const ConciliationContext = createContext(CONCILIATION_CONTEXT_STATE);

export const ConciliationProvider = ({ children }) => {
  const [
    isModalSelectFinancialInstallmentsVisible,
    setIsModalSelectFinancialInstallmentsVisible,
  ] = useState(false);
  const [selectedConciliationTransaction, setSelectedConciliationTransaction] =
    useState(null);

  // Table conciliation properties
  const [selectedRowsKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [dataList, setDataList] = useState([]);
  const [isLoadingDataList, setIsLoadingDataList] = useState(false);
  const [tableProperties, setTableProperties] = useState({
    size: 10,
    page: 0,
    tableFilters: {},
    tableSorters: {},
    search: "",
  });
  // Table select financial installments properties
  const [dataListFinancialInstallments, setDataListFinancialInstallments] =
    useState([]);
  const [
    isLoadingDataListFinancialInstallments,
    setIsLoadingDataListFinancialInstallments,
  ] = useState(false);
  const [
    tablePropertiesFinancialInstallments,
    setTablePropertiesFinancialInstallments,
  ] = useState({
    size: 10,
    page: 0,
    tableFilters: {},
    tableSorters: {},
    search: "",
    idsNotIncluded: [],
  });

  const {
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const getSortString = useCallback((sorter) => {
    let customSort = sorter;

    if (sorter && Object.entries(sorter).length > 0) {
      const { field } = sorter;

      const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;

      customSort = {
        ...customSort,
        field: fieldS,
      };
    }

    return customSort;
  }, []);

  const getSearchString = useCallback((filters) => {
    let search = "";

    if (filters && Object.entries(filters).length > 0) {
      Object.entries(filters).forEach(([field, value]) => {
        if (value && value.length > 0) {
          const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;
          if (fieldS === "amount" || fieldS === "value") {
            const operatorS =
              value[1] === "equal"
                ? "="
                : value[1] === "greaterThan"
                  ? ">"
                  : value[1] === "greaterOrEqualThan"
                    ? ">="
                    : value[1] === "lessThan"
                      ? "<"
                      : value[1] === "lessOrEqualThan"
                        ? "<="
                        : "=";
            search =
              search === ""
                ? `${fieldS} ${operatorS} '${value[0]}'`
                : `${search};${fieldS} ${operatorS} '${value[0]}'`;
          } else if (
            fieldS === "transaction_date" ||
            fieldS === "billing_date" ||
            fieldS === "expiration_date"
          ) {
            const operatorS =
              value[1] === "equal"
                ? "="
                : value[1] === "greaterThan"
                  ? ">"
                  : value[1] === "greaterOrEqualThan"
                    ? ">="
                    : value[1] === "lessThan"
                      ? "<"
                      : value[1] === "lessOrEqualThan"
                        ? "<="
                        : "=";

            search =
              search === ""
                ? `cast(${fieldS} as date) ${operatorS} cast('${moment(
                    value[0],
                  ).format("YYYY-MM-DD")}' as date)`
                : `${search};cast(${fieldS} as date) ${operatorS} cast('${moment(
                    value[0],
                  ).format("YYYY-MM-DD")}' as date)`;
          } else if (fieldS === "type" || fieldS === "transaction_type") {
            search =
              search === ""
                ? `${fieldS} in (${value
                    .map((o) => (o = `'${o}'`))
                    .join(", ")})`
                : `${search};${fieldS} in (${value
                    .map((o) => (o = `'${o}'`))
                    .join(", ")})`;
          } else if (fieldS === "name" || fieldS === "memo") {
            search =
              search === ""
                ? `(upper(name) like upper('%25${value}%25') OR upper(memo) like upper('%25${value}%25'))`
                : `${search};(upper(name) like upper('%25${value}%25') OR upper(memo) like upper('%25${value}%25'))`;
          } else if (fieldS === "conciliated") {
            search =
              search === ""
                ? `should_conciliate = true;${fieldS} = ${value}`
                : `${search};should_conciliate = true;${fieldS} = ${value}`;
          } else {
            search =
              search === ""
                ? `upper(${fieldS}) like upper('%25${value}%25')`
                : `${search};upper(${fieldS}) like upper('%25${value}%25')`;
          }
        }
      });
    }

    return search !== "" ? search : "";
  }, []);

  const closeModalSelectFinancialInstallments = useCallback(() => {
    setSelectedConciliationTransaction(null);
    setIsModalSelectFinancialInstallmentsVisible(false);
  }, []);

  // Table conciliation methods

  const fetchData = useCallback(
    async (page = 0, size = 10, sort = {}, search = "") => {
      setIsLoadingDataList(true);
      try {
        const {
          data: { results },
        } = await generateConciliationTransactionsMatch({
          groupId,
          farmId,
          withoutPagination: true,
        });
        setDataList(results);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingDataList(false);
      }
    },
    [farmId, groupId],
  );

  const handleTableChange = useCallback(
    async (pagination = null, filters = null, sorter = null) => {
      if (pagination && filters && sorter) {
        let search = getSearchString(filters);
        setTableProperties({
          tableFilters: filters,
          tableSorters: sorter,
          size: pagination.pageSize,
          page: pagination.current,
          search,
        });
      } else {
        setTableProperties({
          size: 10,
          page: 0,
          tableFilters: {},
          tableSorters: {},
          search: null,
        });
      }
    },
    [getSearchString],
  );

  // Table select financial installments methods

  const fetchDataFinancialInstallments = useCallback(
    async (
      page = 0,
      size = 10,
      sort = {},
      search = "",
      idsNotIncluded = [],
    ) => {
      setIsLoadingDataListFinancialInstallments(true);
      try {
        const {
          data: { results },
        } = await pageFinancialTransactionsInstallmetnsForConciliation({
          groupId,
          farmId,
          page,
          size,
          sort,
          search,
          idsNotIncluded,
        });
        setDataListFinancialInstallments(results);
      } catch (error) {
        console.error("fetchDataFinancialInstallments", error);
      } finally {
        setIsLoadingDataListFinancialInstallments(false);
      }
    },
    [farmId, groupId],
  );

  const handleTableChangeFinancialInstallments = useCallback(
    (pagination = null, filters = null, sorter = null) => {
      if (pagination && filters && sorter) {
        let customSort = getSortString(sorter);
        let search = getSearchString(filters);

        let idsNotIncluded = [];
        if (selectedConciliationTransaction) {
          const bankAccountFarmId =
            selectedConciliationTransaction.statementTransactionBankAccountFarmId;
          const searchByBankAccountFarmId =
            bankAccountFarmId && bankAccountFarmId !== "null"
              ? `bank_account_farm_id = '${bankAccountFarmId}';`
              : "";
          const transactionsIdsAlreadySelected = dataList.flatMap((statement) =>
            statement.bovexoFinancialTransactionsInstallments.map(
              (o) => o.financialTransactionMainInstallmentId,
            ),
          );
          idsNotIncluded = transactionsIdsAlreadySelected;
          const searchSelectedStatement = [
            "DEBIT",
            "PAYMENT",
            "DIRECTDEBIT",
            "DEP",
          ].includes(selectedConciliationTransaction.statementTransactionType)
            ? `${searchByBankAccountFarmId}transaction_type = 'expense'`
            : selectedConciliationTransaction.statementTransactionType ===
                "CREDIT"
              ? `${searchByBankAccountFarmId}transaction_type = 'income'`
              : `${searchByBankAccountFarmId}`;
          search =
            search !== ""
              ? `${search};${searchSelectedStatement}`
              : searchSelectedStatement;
        }

        setTablePropertiesFinancialInstallments({
          tableFilters: filters || {},
          tableSorters: sorter || {},
          size: pagination.pageSize,
          page: pagination.current,
          search,
          idsNotIncluded,
        });

        fetchDataFinancialInstallments(
          pagination.current,
          pagination.pageSize,
          customSort,
          search,
          idsNotIncluded,
        );
      } else {
        setTablePropertiesFinancialInstallments({
          size: 10,
          page: 0,
          tableFilters: {},
          tableSorters: {},
          search: null,
          idsNotIncluded: [],
        });
        fetchData(0, 10, {}, "");
      }
    },
    [
      dataList,
      fetchData,
      fetchDataFinancialInstallments,
      getSearchString,
      getSortString,
      selectedConciliationTransaction,
    ],
  );

  const showModalSelectFinancialInstallments = useCallback(
    (conciliationTransaction) => {
      const bankAccountFarmId =
        conciliationTransaction.statementTransactionBankAccountFarmId;
      const searchByBankAccountFarmId =
        bankAccountFarmId && bankAccountFarmId !== "null"
          ? `bank_account_farm_id = '${bankAccountFarmId}';`
          : "";
      const transactionsIdsAlreadySelected = dataList.flatMap((statement) =>
        statement.bovexoFinancialTransactionsInstallments.map(
          (o) => o.financialTransactionMainInstallmentId,
        ),
      );
      const idsNotIncluded = transactionsIdsAlreadySelected;
      const search = ["DEBIT", "PAYMENT", "DIRECTDEBIT"].includes(
        conciliationTransaction.statementTransactionType,
      )
        ? `${searchByBankAccountFarmId}transaction_type = 'expense'`
        : conciliationTransaction.statementTransactionType === "CREDIT"
          ? `${searchByBankAccountFarmId}transaction_type = 'income'`
          : `${searchByBankAccountFarmId}`;

      setSelectedConciliationTransaction(conciliationTransaction);
      setTablePropertiesFinancialInstallments({
        size: 10,
        page: 0,
        tableFilters: {},
        tableSorters: {},
        search: search,
        idsNotIncluded: idsNotIncluded,
      });
      fetchDataFinancialInstallments(0, 10, {}, search, idsNotIncluded);
      setIsModalSelectFinancialInstallmentsVisible(true);
    },
    [dataList, fetchDataFinancialInstallments],
  );

  const updateStatementTransactions = useCallback(
    (keys, records) => {
      if (selectedConciliationTransaction) {
        const newDataList = dataList.map((item) => {
          if (selectedConciliationTransaction.id === item.id) {
            return {
              ...item,
              bovexoFinancialTransactionsInstallments: records,
            };
          }
          return item;
        });
        setDataList(newDataList);
      }
    },
    [dataList, selectedConciliationTransaction],
  );

  const removeFinancialTransactionInstallment = useCallback(
    (statementId, financialTransactionMainInstallmentId) => {
      const newDataList = dataList.map((item) => {
        if (item.id === statementId) {
          return {
            ...item,
            bovexoFinancialTransactionsInstallments:
              item.bovexoFinancialTransactionsInstallments.filter(
                (installment) =>
                  installment.financialTransactionMainInstallmentId !==
                  financialTransactionMainInstallmentId,
              ),
          };
        }
        return item;
      });
      setDataList(newDataList);
    },
    [dataList],
  );

  const values = {
    isModalSelectFinancialInstallmentsVisible,
    showModalSelectFinancialInstallments,
    closeModalSelectFinancialInstallments,
    selectedConciliationTransaction,
    // Table conciliation properties
    selectedRowsKeys,
    setSelectedRowKeys,
    selectedRows,
    setSelectedRows,
    dataList,
    isLoadingDataList,
    tableProperties,
    fetchData,
    handleTableChange,
    // Table select financial installments properties
    dataListFinancialInstallments,
    isLoadingDataListFinancialInstallments,
    tablePropertiesFinancialInstallments,
    fetchDataFinancialInstallments,
    handleTableChangeFinancialInstallments,
    updateStatementTransactions,
    removeFinancialTransactionInstallment,
  };

  return (
    <ConciliationContext.Provider value={values}>
      {children}
    </ConciliationContext.Provider>
  );
};
